import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from 'oidc-react';
import { ControlService } from '@/services/control.service';
import { useApiClient } from '@/hooks/useApiClient';
import { useAddedTagSetToast } from '@/hooks/useAddedTagSetToast';
import { Control, CreateControlRequest, CreateControlInstanceRequest, ControlFilters, AddDocumentToControlResponse } from '@/types';

export const useControls = (page: number = 1, limit: number = 20, filters: ControlFilters = {}) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { userData } = useAuth();
  // Ensure token is set on apiClient BEFORE queries fire
  useApiClient();
  const hasToken = !!userData?.access_token;

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['controls', page, limit, filters],
    queryFn: () => ControlService.getControls(page, limit, filters),
    staleTime: 15 * 60 * 1000, // 15 minutes - longer cache time
    gcTime: 20 * 60 * 1000, // 20 minutes - keep in cache longer (renamed from cacheTime in v5)
    refetchInterval: false, // Explicitly disable polling
    refetchOnWindowFocus: false,
    refetchOnMount: 'always', // Always refetch on mount to prevent stale data on dashboard (AG-117)
    enabled: hasToken, // Only run query if access token is available
  });

  const createControlMutation = useMutation({
    mutationFn: (controlData: CreateControlRequest) => ControlService.createControl(controlData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controls'] });
      queryClient.invalidateQueries({ queryKey: ['control-stats'] });
      queryClient.invalidateQueries({ queryKey: ['recentActivities'] });
      enqueueSnackbar('Kontrollen är skapad', { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.message || 'Kunde inte skapa kontroll',
        { variant: 'error' }
      );
    },
  });

  const createControlInstanceMutation = useMutation({
    mutationFn: (controlData: CreateControlInstanceRequest) => ControlService.createControlInstance(controlData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controls'] });
      queryClient.invalidateQueries({ queryKey: ['control-stats'] });
      queryClient.invalidateQueries({ queryKey: ['recentActivities'] });
      enqueueSnackbar('Kontroll tillagd', { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.message || 'Kunde inte skapa kontrollinstans',
        { variant: 'error' }
      );
    },
  });

  const updateControlMutation = useMutation({
    mutationFn: ({ controlId, updates }: { controlId: string; updates: Partial<Control> }) =>
      ControlService.updateControl(controlId, updates),
    onSuccess: (updatedControl) => {
      queryClient.invalidateQueries({ queryKey: ['controls'] });
      queryClient.invalidateQueries({ queryKey: ['control', updatedControl.id] });
      // Toast removed - specific actions show their own success messages
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.message || 'Kunde inte uppdatera kontroll',
        { variant: 'error' }
      );
    },
  });

  const deleteControlMutation = useMutation({
    mutationFn: (controlId: string) => ControlService.deleteControl(controlId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controls'] });
      queryClient.invalidateQueries({ queryKey: ['control-stats'] });
      queryClient.invalidateQueries({ queryKey: ['recentActivities'] });
      enqueueSnackbar('Kontroll borttagen', { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.message || 'Kunde inte ta bort kontroll',
        { variant: 'error' }
      );
    },
  });

  return {
    // Data
    controls: data?.controls || [],
    pagination: data?.pagination,
    
    // Loading states
    isLoading,
    isCreating: createControlMutation.isPending,
    isCreatingInstance: createControlInstanceMutation.isPending,
    isUpdating: updateControlMutation.isPending,
    isDeleting: deleteControlMutation.isPending,
    
    // Error states
    error,
    createError: createControlMutation.error,
    createInstanceError: createControlInstanceMutation.error,
    updateError: updateControlMutation.error,
    deleteError: deleteControlMutation.error,
    
    // Actions
    refetch,
    createControl: createControlMutation.mutate,
    createControlInstance: createControlInstanceMutation.mutate,
    updateControl: updateControlMutation.mutateAsync,
    deleteControl: deleteControlMutation.mutate,
  };
};

export const useControl = (controlId: string) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const reportAddedTagSets = useAddedTagSetToast();
  const { userData } = useAuth();
  // Ensure token is set on apiClient BEFORE queries fire
  useApiClient();
  const hasToken = !!userData?.access_token;

  const {
    data: control,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['control', controlId],
    queryFn: () => ControlService.getControlById(controlId),
    enabled: !!controlId && hasToken,
  });

  const addDocumentMutation = useMutation({
    mutationFn: ({ documentId, requirementId }: { documentId: string; requirementId?: string }) =>
      ControlService.addDocumentToControl(controlId, documentId, requirementId),
    onSuccess: (result: AddDocumentToControlResponse) => {
      queryClient.invalidateQueries({ queryKey: ['control', controlId] });
      // Also invalidate any controls that were auto-linked
      result.linkedControls.forEach(linked => {
        queryClient.invalidateQueries({ queryKey: ['control', linked.controlId] });
      });
      queryClient.invalidateQueries({ queryKey: ['controls'] });
      // Invalidate document sharing info
      queryClient.invalidateQueries({ queryKey: ['documentSharing'] });
      enqueueSnackbar(result.message, { variant: 'success' });
      reportAddedTagSets(result.addedTagSet ? [result.addedTagSet] : []);
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.message || 'Kunde inte lägga till dokument',
        { variant: 'error' }
      );
    },
  });

  const removeDocumentMutation = useMutation({
    mutationFn: (documentId: string) =>
      ControlService.removeDocumentFromControl(controlId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['control', controlId] });
      queryClient.invalidateQueries({ queryKey: ['documentSharing'] });
      enqueueSnackbar('Dokumentet är borttaget från kontrollen', { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.message || 'Kunde inte ta bort dokument',
        { variant: 'error' }
      );
    },
  });

  const unlinkDocumentMutation = useMutation({
    mutationFn: (documentId: string) =>
      ControlService.unlinkDocumentFromControl(controlId, documentId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['control', controlId] });
      queryClient.invalidateQueries({ queryKey: ['documentSharing'] });
      enqueueSnackbar(result.message, { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.message || 'Kunde inte ta bort dokumentlänk',
        { variant: 'error' }
      );
    },
  });

  return {
    // Data
    control,

    // Loading states
    isLoading,
    isAddingDocument: addDocumentMutation.isPending,
    isRemovingDocument: removeDocumentMutation.isPending,
    isUnlinkingDocument: unlinkDocumentMutation.isPending,

    // Error states
    error,
    addDocumentError: addDocumentMutation.error,
    removeDocumentError: removeDocumentMutation.error,
    unlinkDocumentError: unlinkDocumentMutation.error,

    // Actions
    refetch,
    addDocument: addDocumentMutation.mutate,
    addDocumentAsync: addDocumentMutation.mutateAsync,
    removeDocument: removeDocumentMutation.mutate,
    unlinkDocument: unlinkDocumentMutation.mutate,
    unlinkDocumentAsync: unlinkDocumentMutation.mutateAsync,
  };
};

/**
 * Activate a farm-profile-filtered requirement so the farmer can upload
 * documents to it despite it being hidden by the farm profile filter.
 *
 * Invalidates the ['control', controlId] query on success.
 */
export const useActivateRequirement = (controlId: string) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (requirementId: string) =>
      ControlService.activateRequirement(controlId, requirementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['control', controlId] });
      queryClient.invalidateQueries({ queryKey: ['controls'] });
      enqueueSnackbar('Kravet är aktiverat', { variant: 'success' });
    },
    onError: (error: any) => {
      const message = error?.message || 'Kunde inte aktivera kravet';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
};

/**
 * Activate a farm-profile-filtered form field so the farmer can answer it.
 *
 * Invalidates ['controls'] too: activating a field changes the required-field
 * count, so the progress shown in the control list would otherwise go stale.
 */
export const useActivateFormField = (controlId: string) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (fieldId: string) => ControlService.activateFormField(controlId, fieldId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['control', controlId] });
      queryClient.invalidateQueries({ queryKey: ['controls'] });
      enqueueSnackbar('Fältet är aktiverat', { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(error?.message || 'Kunde inte aktivera fältet', { variant: 'error' });
    },
  });
};

/**
 * Deactivate a previously activated form field, putting it back under the
 * farm-profile filter.
 *
 * The backend responds 409 when the field still holds an answer or has a
 * document attached. The caller guards client-side first (see `isFieldSatisfied`)
 * and handles the ApiError for the race where either arrived in another tab.
 *
 * Invalidates the ['control', controlId] and ['controls'] queries on success.
 */
export const useDeactivateFormField = (controlId: string) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (fieldId: string) => ControlService.deactivateFormField(controlId, fieldId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['control', controlId] });
      queryClient.invalidateQueries({ queryKey: ['controls'] });
      enqueueSnackbar('Fältet är inte längre aktivt', { variant: 'success' });
    },
    onError: (error: any) => {
      // 409 means the field holds an answer or a document — caller handles dialog.
      if (error?.statusCode === 409) return;
      // 404 means the activation row is already gone (deactivated in another tab):
      // the state is what the user asked for, so refresh rather than complain.
      if (error?.statusCode === 404) {
        queryClient.invalidateQueries({ queryKey: ['control', controlId] });
        queryClient.invalidateQueries({ queryKey: ['controls'] });
        enqueueSnackbar('Fältet är inte längre aktivt', { variant: 'success' });
        return;
      }
      enqueueSnackbar(error?.message || 'Kunde inte deaktivera fältet', { variant: 'error' });
    },
  });
};

/**
 * Deactivate a previously activated requirement.
 * If the requirement has linked documents, the backend responds with 409 —
 * the caller should guard client-side first and handle the ApiError on receipt.
 *
 * Invalidates the ['control', controlId] query on success.
 */
export const useDeactivateRequirement = (controlId: string) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (requirementId: string) =>
      ControlService.deactivateRequirement(controlId, requirementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['control', controlId] });
      queryClient.invalidateQueries({ queryKey: ['controls'] });
      enqueueSnackbar('Kravet är inte längre aktivt', { variant: 'success' });
    },
    onError: (error: any) => {
      // 409 means the requirement has linked documents — caller handles dialog.
      if (error?.statusCode === 409) return;
      // 404 means the activation row is already gone (deactivated in another tab):
      // the state is what the user asked for, so refresh rather than complain.
      if (error?.statusCode === 404) {
        queryClient.invalidateQueries({ queryKey: ['control', controlId] });
        queryClient.invalidateQueries({ queryKey: ['controls'] });
        enqueueSnackbar('Kravet är inte längre aktivt', { variant: 'success' });
        return;
      }
      enqueueSnackbar(error?.message || 'Kunde inte deaktivera kravet', { variant: 'error' });
    },
  });
};

export const useControlAnalysis = (controlId: string) => {
  const { userData } = useAuth();
  // Ensure token is set on apiClient BEFORE queries fire
  useApiClient();
  const hasToken = !!userData?.access_token;

  const { data: analysis, isLoading, error, refetch } = useQuery({
    queryKey: ['control-analysis', controlId],
    queryFn: () => ControlService.getControlAnalysis(Number(controlId)),
    enabled: !!controlId && hasToken,
  });

  return {
    analysis,
    isLoading,
    error,
    refetch,
  };
};

export const useControlStats = () => {
  const { userData } = useAuth();
  // Ensure token is set on apiClient BEFORE queries fire
  useApiClient();
  const hasToken = !!userData?.access_token;

  const { data: stats, isLoading, error, refetch } = useQuery({
    queryKey: ['control-stats'],
    queryFn: () => ControlService.getControlStats(),
    staleTime: 20 * 60 * 1000, // 20 minutes - stats don't change that often
    gcTime: 25 * 60 * 1000, // 25 minutes - keep in cache longer (renamed from cacheTime in v5)
    refetchInterval: false, // Explicitly disable polling
    refetchOnWindowFocus: false,
    enabled: hasToken, // Only run query if access token is available
  });

  return {
    stats,
    isLoading,
    error,
    refetch,
  };
};